import { useCallback, useEffect, useState } from 'react';
import { Icon } from './Icon';

export interface AccessInfo {
  userId: string;
  phone: string;
  hasAccess: boolean;
  reason: 'trial' | 'subscription' | 'none' | 'pending_payment';
  trialEndsAt: string | null;
  subscriptionUntil: string | null;
  paymentId: string | null;
  plan: {
    priceRub: number;
    days: number;
    trialDays: number;
    title: string;
  };
}

const formatDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' }) : null;

type AuthMode = 'login' | 'register' | 'reset';

export const AuthGate = ({ onAccess }: { onAccess: (access: AccessInfo) => void }) => {
  const [mode, setMode] = useState<AuthMode>('login');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [access, setAccess] = useState<AccessInfo | null>(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [payError, setPayError] = useState('');
  const [resetStep, setResetStep] = useState<'phone' | 'code'>('phone');
  const [resetPhone, setResetPhone] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetError, setResetError] = useState('');

  const loadMe = useCallback(async () => {
    try {
      const response = await fetch('/api/me');
      if (response.status === 401) return null;
      const payload = (await response.json()) as AccessInfo;
      setAccess(payload);
      onAccess(payload);
      return payload;
    } catch {
      return null;
    }
  }, [onAccess]);

  useEffect(() => {
    // После возврата с ЮKassa — проверяем статус платежа
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'return') {
      setCheckingPayment(true);
      window.history.replaceState(null, '', '/');
      void fetch('/api/billing/status', { method: 'POST' })
        .then((r) => (r.ok ? r.json() : null))
        .then((payload) => payload as AccessInfo | null)
        .then((payload) => {
          if (payload) {
            setAccess(payload);
            onAccess(payload);
          }
        })
        .catch(() => undefined)
        .finally(() => setCheckingPayment(false));
      return;
    }
    void loadMe();
  }, [loadMe, onAccess]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const response = await fetch(`/api/auth/${mode}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, password, email }),
      });
      const payload = await response.json();
      if (!response.ok) {
        setError(payload.error ?? 'Не удалось выполнить запрос');
        return;
      }
      const info = payload as AccessInfo;
      setAccess(info);
      onAccess(info);
    } catch {
      setError('Сервер недоступен, попробуйте ещё раз');
    } finally {
      setBusy(false);
    }
  };

  const startPayment = async () => {
    setBusy(true);
    setPayError('');
    try {
      const response = await fetch('/api/billing/pay', { method: 'POST' });
      const payload = await response.json();
      if (!response.ok || !payload.confirmationUrl) {
        setPayError(payload.error ?? 'Не удалось создать платёж');
        return;
      }
      window.location.href = payload.confirmationUrl;
    } catch {
      setPayError('Платёжный сервис недоступен');
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await fetch('/api/auth/logout', { method: 'POST' }).catch(() => undefined);
    window.location.reload();
  };

  const requestResetCode = async () => {
    setBusy(true);
    setResetError('');
    try {
      const response = await fetch('/api/auth/reset-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: resetPhone }),
      });
      const payload = await response.json();
      if (!response.ok) {
        setResetError(payload.error ?? 'Не удалось отправить код');
        return;
      }
      if (payload.requiredEmail) {
        setResetError('К этому аккаунту не привязан email. Войдите и укажите его, либо обратитесь в поддержку.');
        return;
      }
      setResetStep('code');
    } catch {
      setResetError('Сервер недоступен, попробуйте ещё раз');
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async () => {
    setBusy(true);
    setResetError('');
    try {
      const response = await fetch('/api/auth/reset-confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: resetPhone, code: resetCode, password: resetNewPassword }),
      });
      const payload = await response.json();
      if (!response.ok) {
        setResetError(payload.error ?? 'Не удалось сменить пароль');
        return;
      }
      setAccess(payload);
      onAccess(payload);
    } catch {
      setResetError('Сервер недоступен, попробуйте ещё раз');
    } finally {
      setBusy(false);
    }
  };

  const ruNumber = (value: number) => value.toLocaleString('ru-RU');

  // ===== Экран входа =====
  if (!access) {
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <div className="topbar-brand auth-brand">
            <span className="brand-mark"><i /><i /><i /><i /></span>
            <span><strong>KAFEL</strong><b>LAB</b></span>
          </div>
          <h1>Визуализатор раскладки плитки</h1>
          <p className="auth-lead">
            Загрузите фото плитки, задайте размеры комнаты — получите точную раскладку,
            подрезки, смету материала и PDF-расчёт для клиента.
          </p>
          <ul className="auth-features">
            <li><Icon name="check" /> Реальные размеры в миллиметрах, 2D и 3D-просмотр</li>
            <li><Icon name="check" /> Загрузка фото своей плитки, 5 типов раскладки</li>
            <li><Icon name="check" /> Расчёт материала и стоимости, экспорт PNG/JPG/PDF</li>
            <li><Icon name="check" /> Проекты сохраняются в облаке — доступны с любого устройства</li>
          </ul>
          {checkingPayment && <p className="auth-note">Проверяем статус оплаты…</p>}
          {mode === 'reset' ? (
            <>
              {resetStep === 'phone' ? (
                <form onSubmit={(event) => { event.preventDefault(); void requestResetCode(); }} className="auth-form">
                  <label className="field">
                    <span>Телефон или email аккаунта</span>
                    <span className="input-shell">
                      <input
                        type="text"
                        value={resetPhone}
                        onChange={(event) => setResetPhone(event.target.value)}
                        placeholder="+7 (___) ___-__-__ или you@example.com"
                        autoComplete="username"
                        required
                      />
                    </span>
                  </label>
                  {resetError && <p className="auth-error">{resetError}</p>}
                  <button type="submit" className="button primary auth-submit" disabled={busy}>
                    Отправить код
                  </button>
                  <p className="auth-link-note">Код придёт на email, привязанный к аккаунту.</p>
                </form>
              ) : (
                <form onSubmit={(event) => { event.preventDefault(); void submitReset(); }} className="auth-form">
                  <label className="field">
                    <span>Код из письма</span>
                    <span className="input-shell">
                      <input
                        type="text"
                        value={resetCode}
                        onChange={(event) => setResetCode(event.target.value)}
                        placeholder="6 цифр"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        required
                      />
                    </span>
                  </label>
                  <label className="field">
                    <span>Новый пароль</span>
                    <span className="input-shell">
                      <input
                        type="password"
                        value={resetNewPassword}
                        onChange={(event) => setResetNewPassword(event.target.value)}
                        placeholder="Минимум 6 символов"
                        autoComplete="new-password"
                        required
                        minLength={6}
                      />
                    </span>
                  </label>
                  {resetError && <p className="auth-error">{resetError}</p>}
                  <button type="submit" className="button primary auth-submit" disabled={busy}>
                    Сменить пароль
                  </button>
                </form>
              )}
            </>
          ) : (
            <>
              <form onSubmit={submit} className="auth-form">
                <label className="field">
                  <span>Телефон</span>
                  <span className="input-shell">
                    <input
                      type="tel"
                      value={phone}
                      onChange={(event) => setPhone(event.target.value)}
                      placeholder="+7 (___) ___-__-__"
                      autoComplete="tel"
                      required
                    />
                  </span>
                </label>
                <label className="field">
                  <span>Пароль</span>
                  <span className="input-shell">
                    <input
                      type="password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      placeholder="Минимум 6 символов"
                      autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                      required
                      minLength={6}
                    />
                  </span>
                </label>
                {mode === 'register' && (
                  <label className="field">
                    <span>Email</span>
                    <span className="input-shell">
                      <input
                        type="email"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        placeholder="you@example.com"
                        autoComplete="email"
                        required
                      />
                    </span>
                  </label>
                )}
                {error && <p className="auth-error">{error}</p>}
                <button type="submit" className="button primary auth-submit" disabled={busy}>
                  {mode === 'login' ? 'Войти' : 'Зарегистрироваться'}
                </button>
              </form>
              {mode === 'login' && (
                <button
                  type="button"
                  className="auth-switch"
                  onClick={() => { setMode('reset'); setError(''); setResetError(''); setResetStep('phone'); }}
                >
                  Забыли пароль?
                </button>
              )}
            </>
          )}
          {mode !== 'reset' && (
            <button
              type="button"
              className="auth-switch"
              onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); }}
            >
              {mode === 'login'
                ? 'Нет аккаунта? Зарегистрируйтесь — 3 дня бесплатно'
                : 'Уже есть аккаунт? Войдите'}
            </button>
          )}
          {mode === 'reset' && (
            <button
              type="button"
              className="auth-switch"
              onClick={() => { setMode('login'); setResetError(''); setResetStep('phone'); }}
            >
              ← Назад ко входу
            </button>
          )}
          <p className="auth-pricing">
            Тариф: {ruNumber(990)} ₽ / 30 дней.
            Первые 3 дня — бесплатно.
          </p>
          <p className="auth-pricing">
            <a href="/oferta" target="_blank" rel="noreferrer">Публичная оферта</a>
            {' · '}
            <a href="/privacy" target="_blank" rel="noreferrer">Политика конфиденциальности</a>
          </p>
        </div>
      </div>
    );
  }

  // ===== Экран «нет доступа» (триал кончился / не начат, подписка не активна) =====
  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="topbar-brand auth-brand">
          <span className="brand-mark"><i /><i /><i /><i /></span>
          <span><strong>KAFEL</strong><b>LAB</b></span>
        </div>
        <h1>
          {access.reason === 'pending_payment' ? 'Проверяем оплату…' : 'Доступ закрыт'}
        </h1>
        {access.reason === 'pending_payment' ? (
          <>
            <p className="auth-lead">
              Мы получили возврат от платёжной страницы. Нажмите кнопку ниже, чтобы
              подтвердить оплату и активировать доступ.
            </p>
            <button
              type="button"
              className="button primary auth-submit"
              disabled={checkingPayment}
              onClick={async () => {
                setCheckingPayment(true);
                try {
                  const response = await fetch('/api/billing/status', { method: 'POST' });
                  const payload = (await response.json()) as AccessInfo;
                  setAccess(payload);
                  onAccess(payload);
                } finally {
                  setCheckingPayment(false);
                }
              }}
            >
              Проверить оплату
            </button>
          </>
        ) : (
          <>
            <p className="auth-lead">
              Пробный период{access.trialEndsAt ? ` (до ${formatDate(access.trialEndsAt)})` : ''} закончился.
              Оформите доступ, чтобы продолжить работать с проектами.
            </p>
            <div className="auth-plan-card">
              <strong>{access.plan.title}</strong>
              <span className="auth-plan-price">{ruNumber(access.plan.priceRub)} ₽</span>
              <ul>
                <li><Icon name="check" /> Безлимитные проекты в облаке</li>
                <li><Icon name="check" /> Экспорт PNG, JPG и PDF-расчёта</li>
                <li><Icon name="check" /> Доступ с любого устройства</li>
              </ul>
              {payError && <p className="auth-error">{payError}</p>}
              <button
                type="button"
                className="button primary auth-submit"
                disabled={busy}
                onClick={() => void startPayment()}
              >
                <Icon name="download" /> Оплатить {ruNumber(access.plan.priceRub)} ₽
              </button>
            </div>
          </>
        )}
        <div className="auth-footer-row">
          <span>{access.phone}</span>
          <button type="button" className="auth-switch" onClick={() => void logout()}>
            Выйти
          </button>
        </div>
      </div>
    </div>
  );
};
